import React from 'react';
import type { MigrationStage, MigrationLogEntry } from '../../types/logs';

interface StageSummaryCardProps {
  /** The stage being summarized */
  stage: MigrationStage;
  /** All logs to calculate summary from */
  logs: MigrationLogEntry[];
  /** Optional className for customization */
  className?: string;
}

interface StageSummary {
  successCount: number;
  errorCount: number;
  warningCount: number;
  infoCount: number;
  total: number;
  status: 'success' | 'partial' | 'failed' | 'in-progress';
}

/**
 * Calculate summary statistics for a stage
 */
const calculateStageSummary = (logs: MigrationLogEntry[], stage: MigrationStage): StageSummary => {
  const stageLogs = logs.filter((log) => log.stage === stage);

  const summary = {
    successCount: stageLogs.filter((log) => log.level === 'Success').length,
    errorCount: stageLogs.filter((log) => log.level === 'Error').length,
    warningCount: stageLogs.filter((log) => log.level === 'Warning').length,
    infoCount: stageLogs.filter((log) => log.level === 'Info').length,
    total: stageLogs.length
  };

  // Determine overall status
  let status: 'success' | 'partial' | 'failed' | 'in-progress' = 'in-progress';

  if (summary.errorCount > 0 && summary.successCount === 0) {
    status = 'failed';
  } else if (summary.errorCount > 0 && summary.successCount > 0) {
    status = 'partial';
  } else if (summary.successCount > 0 && summary.errorCount === 0) {
    status = 'success';
  }

  return { ...summary, status };
};

/**
 * Get status display configuration
 */
const getStatusConfig = (status: StageSummary['status']) => {
  switch (status) {
    case 'success':
      return {
        label: 'Successful',
        icon: '✓',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-700',
        textColor: 'text-green-800 dark:text-green-200',
        iconColor: 'text-green-600 dark:text-green-400'
      };
    case 'partial':
      return {
        label: 'Partially Successful',
        icon: '⚠',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-700',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        iconColor: 'text-yellow-600 dark:text-yellow-400'
      };
    case 'failed':
      return {
        label: 'Failed',
        icon: '✗',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-700',
        textColor: 'text-red-800 dark:text-red-200',
        iconColor: 'text-red-600 dark:text-red-400'
      };
    case 'in-progress':
      return {
        label: 'In Progress',
        icon: '↻',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-700',
        textColor: 'text-blue-800 dark:text-blue-200',
        iconColor: 'text-blue-600 dark:text-blue-400'
      };
  }
};

/**
 * Display summary card for a specific migration stage
 * Shows aggregate statistics and overall status
 */
export const StageSummaryCard: React.FC<StageSummaryCardProps> = ({
  stage,
  logs,
  className = ''
}) => {
  const summary = calculateStageSummary(logs, stage);
  const statusConfig = getStatusConfig(summary.status);

  if (summary.total === 0) {
    return null; // Don't show card if no logs for this stage
  }

  return (
    <div
      className={`${statusConfig.bgColor} ${statusConfig.borderColor} border-l-4 rounded-lg p-4 mb-6 ${className}`}
      role="status"
      aria-label={`${stage} stage summary`}
    >
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div
          className={`text-2xl ${statusConfig.iconColor} flex-shrink-0 mt-0.5`}
          aria-hidden="true"
        >
          {statusConfig.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className={`text-lg font-semibold ${statusConfig.textColor} mb-1`}>
            {statusConfig.label} {stage}
          </h3>

          {/* Statistics */}
          <div className={`text-sm ${statusConfig.textColor} space-y-1`}>
            {summary.successCount > 0 && (
              <p>
                <span className="font-medium">✓ {summary.successCount}</span> successful
              </p>
            )}
            {summary.errorCount > 0 && (
              <p>
                <span className="font-medium">✗ {summary.errorCount}</span> failed
              </p>
            )}
            {summary.warningCount > 0 && (
              <p>
                <span className="font-medium">⚠ {summary.warningCount}</span> warnings
              </p>
            )}
            {summary.infoCount > 0 && (
              <p className="text-xs opacity-75">{summary.infoCount} informational messages</p>
            )}
          </div>

          {/* Total count */}
          <p className={`text-xs ${statusConfig.textColor} opacity-75 mt-2`}>
            Total: {summary.total} log entries
          </p>
        </div>
      </div>
    </div>
  );
};
